import { memo } from "react";
import { makeStyles } from "tss-react/mui";
import { toKebabCase } from "@miplatform/utils";
import { Tab, Tabs, Tooltip } from "@mui/material";
import { MiChip } from "../MiChip/MiChip";

/**
 * `MiTab` is a memoized functional component that renders a customizable tab interface.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {Array<Object>} props.tabs - An array of tab objects, each containing properties to render a `Tab` component.
 * @param {string} [props.currentTab=""] - The value of the currently selected tab.
 * @param {function} [props.onTabChange=() => null] - A callback function triggered when the tab selection changes.
 * @param {boolean} [props.disabled=false] - A flag indicating whether all tabs are disabled.
 *
 * @returns {JSX.Element} A `Tabs` component with individual `Tab` elements.
 */

const TooltipWrapper = ({ tab, children }) => {
  return (
    <Tooltip title={tab.tooltip}>
      <span>{children}</span>
    </Tooltip>
  );
};

const renderChip = (tab, chipStyle) => {
  return tab.chipLabel != null ? (
    <MiChip
      label={tab.chipLabel}
      size="small"
      color={tab.chipColor || "error"}
      sx={{ height: 18, fontSize: 12, ...chipStyle }}
    />
  ) : null;
};

/**
 * `MiTab` is a memoized functional component that renders a customizable scrollable tab interface.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {Array<{label: string, value: string, disabled?: boolean}>} [props.tabs=[]] - An array of tab objects, each containing properties to render a `Tab` component.
 * @param {string | number} [props.currentTab=""] - The value of the currently selected tab.
 * @param {function} [props.onTabChange=() => null] - A callback function triggered when the tab selection changes.
 * @param {boolean} [props.isSecondary=false] - Whether to apply secondary styling (affects selected tab background).
 * @param {boolean} [props.disabled=false] - A flag indicating whether all tabs are disabled.
 * @param {boolean} [props.borderBottom=false] - Whether to show a border at the bottom of the tabs container.
 * @param {string|boolean} [props.scrollButtons="auto"] - Controls the scroll buttons behavior for the tabs.
 * @param {Object} [props.chipStyle={}] - Additional styles to apply to all chips in the tabs.
 *
 * @returns {JSX.Element} A scrollable `Tabs` component with individual `Tab` elements, optionally wrapped with tooltips.
 */
const MiTabComponent = ({
  tabs = [],
  currentTab = "",
  onTabChange = () => null,
  isSecondary = false,
  disabled = false,
  borderBottom = false,
  scrollButtons = "auto",
  chipStyle = {},
  backgroundColor,
}) => {
  const { classes } = useStyles({ isSecondary, borderBottom, backgroundColor });

  const handleChange = (_, newValue) => {
    onTabChange(newValue);
  };

  return (
    <Tabs
      variant="scrollable"
      scrollButtons={scrollButtons}
      onChange={handleChange}
      value={currentTab}
      className={classes.tabs}
    >
      {tabs.map((tab, index) => {
        const tabElement = (
          <Tab
            key={tab.value || index}
            data-testid={`mitab-${toKebabCase(tab.label)}`}
            className={classes.tab}
            disabled={disabled || tab.disabled}
            value={tab.value}
            label={tab.label}
            icon={tab.icon ?? renderChip(tab, chipStyle)}
            iconPosition={tab.iconPosition ?? "end"}
            sx={tab.sx}
          />
        );

        return tab.tooltip ? (
          <TooltipWrapper key={tab.value} tab={tab}>
            {tabElement}
          </TooltipWrapper>
        ) : (
          tabElement
        );
      })}
    </Tabs>
  );
};

export const MiTab = memo(MiTabComponent);

const useStyles = makeStyles()(
  (theme, { borderBottom, isSecondary, backgroundColor }) => ({
    tabs: {
      borderBottom: borderBottom && `1px solid ${theme.palette.grey.accent}`,
      minHeight: 35,
      backgroundColor: backgroundColor || theme.palette.common.white,
      "& .MuiTabs-indicator": {
        backgroundColor: theme.palette.primary.main,
      },
    },
    tab: {
      minHeight: 35,
      textTransform: "none",
      fontWeight: theme.fontWeight.semiBold,
      "&.Mui-selected": {
        fontWeight: theme.fontWeight.semiBold,
        color: theme.palette.common.black,
        ...(!isSecondary && {
          backgroundColor: theme.palette.primary.light,
        }),
      },
      "&.MuiButtonBase-root": {
        padding: theme.spacing(0, 4),
      },
    },
  })
);
